import { Suspense } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { GitFork } from 'lucide-react';
import {
  useCreateRubric,
  getListRubricsQueryKey,
  getGetRubricQueryOptions,
} from '@sara/api-client';
import { Skeleton } from '@sara/ui/components/skeleton';
import { QueryErrorBoundary } from '@sara/ui/components/query-error-boundary';
import { RubricForm, RubricFormSkeleton, type RubricFormValues } from '../../components/RubricForm';
import { rubricToFormValues } from '../../components/RubricForm/mapping';
import { toSuspense } from '../../../../lib/query';
import { useOrgSlug } from '../../../auth';

interface CreateRubricFormContainerProps {
  orgId: string;
  /** When set, the form is pre-filled from this rubric's latest version. */
  forkFromRubricId?: string;
}

function CreateRubricForm({
  orgId,
  defaultValues,
  forkedFrom,
}: {
  orgId: string;
  defaultValues?: RubricFormValues;
  forkedFrom?: { id: string; name: string; version: number };
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgSlug = useOrgSlug();

  const { mutate, isPending, error } = useCreateRubric({
    mutation: {
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: getListRubricsQueryKey(),
        });
        void navigate({
          to: '/orgs/$orgSlug/rubrics/$rubricId',
          params: { orgSlug, rubricId: data.id },
        });
      },
    },
  });

  function handleSubmit(values: RubricFormValues) {
    mutate({
      data: {
        orgId,
        name: values.name,
        description: values.description,
        dimensions: values.dimensions,
        bands: values.bands,
        flags: values.flags,
      },
    });
  }

  return (
    <div className="space-y-5">
      {forkedFrom && (
        <div
          data-testid="forked-from-banner"
          className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-muted/30 px-4 py-2.5 text-[12.5px] text-muted-foreground"
        >
          <GitFork className="h-3.5 w-3.5 shrink-0 text-foreground/70" strokeWidth={1.75} />
          <span>
            Forking from{' '}
            <Link
              to="/orgs/$orgSlug/rubrics/$rubricId"
              params={{ orgSlug, rubricId: forkedFrom.id }}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {forkedFrom.name}
            </Link>{' '}
            <span className="tabular-nums">v{forkedFrom.version}</span> — this creates an
            independent copy; edit anything before saving.
          </span>
        </div>
      )}
      <RubricForm
        mode="create"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isPending={isPending}
        error={error ? (error instanceof Error ? error.message : 'Failed to create rubric') : null}
      />
    </div>
  );
}

function ForkRubricFormSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-10 rounded-lg" />
      <RubricFormSkeleton />
    </div>
  );
}

function ForkRubricFormContent({
  orgId,
  forkFromRubricId,
}: {
  orgId: string;
  forkFromRubricId: string;
}) {
  const { data: source } = useSuspenseQuery(toSuspense(getGetRubricQueryOptions(forkFromRubricId)));
  const sourceValues = rubricToFormValues(source);

  // A rubric with no versions has nothing to fork — fall back to a blank form.
  if (!sourceValues) {
    return <CreateRubricForm orgId={orgId} />;
  }

  return (
    <CreateRubricForm
      orgId={orgId}
      defaultValues={{ ...sourceValues, name: `Copy of ${source.name}` }}
      forkedFrom={{ id: source.id, name: source.name, version: source.versions[0].version }}
    />
  );
}

export function CreateRubricFormContainer({
  orgId,
  forkFromRubricId,
}: CreateRubricFormContainerProps) {
  const orgSlug = useOrgSlug();

  if (!forkFromRubricId) {
    return <CreateRubricForm orgId={orgId} />;
  }

  return (
    <QueryErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => (
        <div className="space-y-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p>Couldn&apos;t load the rubric to fork — it may have been deleted.</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={resetErrorBoundary}
              className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Try again
            </button>
            <Link
              to="/orgs/$orgSlug/rubrics/new"
              params={{ orgSlug }}
              search={{}}
              className="text-xs font-medium underline-offset-4 hover:underline"
            >
              Start blank instead
            </Link>
          </div>
        </div>
      )}
    >
      <Suspense fallback={<ForkRubricFormSkeleton />}>
        <ForkRubricFormContent orgId={orgId} forkFromRubricId={forkFromRubricId} />
      </Suspense>
    </QueryErrorBoundary>
  );
}
